import { useRef, useState } from "react"
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material"
import DownloadOutlined from "@mui/icons-material/DownloadOutlined"
import FileUploadOutlined from "@mui/icons-material/FileUploadOutlined"
import PapaParse from "papaparse"
import { toast } from "react-toastify"

import {
  FusionProductMappingImportRow,
  ImportIntegrationProductsMutation,
  IntegrationProductsDocument,
  useImportIntegrationProductsMutation,
  useIntegrationProductTemplateLazyQuery
} from "src/graphql/quark/quark.type"

type ImportResult = ImportIntegrationProductsMutation["importFusionIntegrationProducts"]

type BulkMapProductsDialogProps = {
  connectionId: string
  integrationName: string
  open: boolean
  onClose: () => void
}

const PROBLEMS_SHOWN = 50

const normalizeHeader = (header: string) => header.toLowerCase().replace(/[^a-z0-9]/g, "")

const downloadCsv = (fileName: string, fields: string[], data: unknown[][]) => {
  const blob = new Blob([PapaParse.unparse({ fields, data })], {
    type: "text/csv;charset=utf-8"
  })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = fileName
  link.click()
  URL.revokeObjectURL(url)
}

const fileSlug = (name: string) => name.toLowerCase().replace(/[^a-z0-9]+/g, "-")

const isBufferHeader = (header: string) => normalizeHeader(header).includes("buffer")
const isMaxPerOrderHeader = (header: string) =>
  ["maxperorder", "maxsales", "maximumsales"].some((name) => normalizeHeader(header).includes(name))

/**
 * Maps many products at once from a CSV. Every row states a Product ID and the
 * SKU the integration knows it by — nothing is matched automatically. The file
 * is checked first (nothing saved), then saved on confirmation.
 */
const BulkMapProductsDialog = ({ connectionId, integrationName, open, onClose }: BulkMapProductsDialogProps) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [fileName, setFileName] = useState<string | null>(null)
  const [rows, setRows] = useState<FusionProductMappingImportRow[]>([])
  const [blankSkuCount, setBlankSkuCount] = useState(0)
  const [preview, setPreview] = useState<ImportResult | null>(null)

  const [loadTemplate, { loading: templateLoading }] = useIntegrationProductTemplateLazyQuery({
    fetchPolicy: "network-only"
  })
  const [importProducts, { loading: importing }] = useImportIntegrationProductsMutation()

  const reset = () => {
    setFileName(null)
    setRows([])
    setBlankSkuCount(0)
    setPreview(null)
    if (fileInputRef.current) fileInputRef.current.value = ""
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const handleDownloadTemplate = async () => {
    const { data, error } = await loadTemplate({ variables: { connectionId } })
    if (error || !data) {
      toast.error(error?.message ?? "Could not build the template")
      return
    }
    downloadCsv(
      `${fileSlug(integrationName)}-product-mapping.csv`,
      ["Product ID", "Product name", "Status", "SKU", "Stock buffer", "Max per order"],
      data.fusionIntegrationProductTemplate.map((p) => [
        p.productCode,
        p.name,
        p.active ? "Active" : "Inactive",
        p.externalSku ?? "",
        p.stockBuffer ?? "",
        p.maximumSalesQuantity ?? ""
      ])
    )
  }

  const runImport = async (parsed: FusionProductMappingImportRow[], dryRun: boolean) => {
    const result = await importProducts({
      variables: { input: { connectionId, rows: parsed, dryRun } },
      refetchQueries: dryRun ? [] : [{ query: IntegrationProductsDocument, variables: { connectionId } }]
    })
    return result.data?.importFusionIntegrationProducts ?? null
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (!file.name.toLowerCase().endsWith(".csv")) {
      toast.error("Only .csv files are supported.")
      return
    }

    // Empty lines are kept while parsing so row numbers match the file as the
    // operator sees it in a spreadsheet (header is row 1).
    PapaParse.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: false,
      complete: async ({ data, meta }) => {
        const headers = meta.fields ?? []
        const productHeader = headers.find((h) => ["productid", "productcode"].includes(normalizeHeader(h)))
        const skuHeader = headers.find((h) => normalizeHeader(h).includes("sku"))
        // Optional columns: absent ones leave those settings untouched, so an
        // older file cannot wipe them; an empty cell clears the setting.
        const bufferHeader = headers.find(isBufferHeader)
        const maxPerOrderHeader = headers.find(isMaxPerOrderHeader)

        if (!productHeader || !skuHeader) {
          toast.error('The file needs a "Product ID" column and a SKU column.')
          return
        }

        const parsed: FusionProductMappingImportRow[] = []
        let blank = 0
        data.forEach((row, index) => {
          const productCode = (row[productHeader] ?? "").trim()
          const externalSku = (row[skuHeader] ?? "").trim()
          if (!productCode && !externalSku) return
          // The template lists every product; an empty SKU means "leave this
          // one alone", not an error.
          if (!externalSku) {
            blank += 1
            return
          }
          parsed.push({
            rowNumber: index + 2,
            productCode,
            externalSku,
            ...(bufferHeader && { stockBuffer: (row[bufferHeader] ?? "").trim() }),
            ...(maxPerOrderHeader && { maximumSalesQuantity: (row[maxPerOrderHeader] ?? "").trim() })
          })
        })

        setFileName(file.name)
        setRows(parsed)
        setBlankSkuCount(blank)
        setPreview(null)

        if (parsed.length === 0) {
          toast.info("No rows have a SKU filled in, so there is nothing to map.")
          return
        }

        try {
          setPreview(await runImport(parsed, true))
        } catch (error) {
          toast.error(error instanceof Error ? error.message : "Could not check the file")
        }
      },
      error: () => toast.error("Could not read the CSV file.")
    })
  }

  const handleSave = async () => {
    try {
      const result = await runImport(rows, false)
      if (!result) return
      toast.success(`Saved. Changed products go to ${integrationName} with the next push.`)
      handleClose()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not save the mappings")
    }
  }

  const handleDownloadProblems = () => {
    if (!preview) return
    downloadCsv(
      `${fileSlug(integrationName)}-mapping-problems.csv`,
      ["Row", "Product ID", "SKU", "Problem"],
      preview.errors.map((e) => [e.rowNumber, e.productCode, e.externalSku, e.message])
    )
  }

  const hasChanges =
    !!preview &&
    preview.created + preview.updated + preview.stockBuffersChanged + preview.salesLimitsChanged > 0
  const plural = (n: number) => (n === 1 ? "" : "s")

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6" fontWeight={700}>
          Bulk map products
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Link many products to their {integrationName} SKUs from one CSV file.
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          <Stack spacing={1}>
            <Typography variant="subtitle1" fontWeight={600}>
              1. Download the product list
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Every product with its Product ID and current settings. Fill in the SKU column with each product&apos;s{" "}
              {integrationName} SKU for the products you want to publish and leave the rest empty — rows without a SKU are
              ignored. Stock buffer is the units held back (empty uses the integration&apos;s buffer); Max per order is the
              most units one order may take (empty means no limit). In Excel, keep the SKU column formatted as text so
              leading zeros are not lost.
            </Typography>
            <Box>
              <Button
                size="small"
                variant="outlined"
                startIcon={templateLoading ? <CircularProgress size={14} /> : <DownloadOutlined />}
                disabled={templateLoading}
                onClick={() => void handleDownloadTemplate()}
                sx={{ textTransform: "none" }}
              >
                Download product list
              </Button>
            </Box>
          </Stack>

          <Stack spacing={1}>
            <Typography variant="subtitle1" fontWeight={600}>
              2. Upload the filled-in file
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Nothing is saved yet — the file is checked first and you see what will change.
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <input
                ref={fileInputRef}
                id="bulk-map-products-input"
                type="file"
                accept=".csv"
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
              <label htmlFor="bulk-map-products-input">
                <Button
                  component="span"
                  size="small"
                  variant="contained"
                  startIcon={<FileUploadOutlined />}
                  disabled={importing}
                  sx={{ textTransform: "none" }}
                >
                  {fileName ? "Choose another file" : "Choose CSV file"}
                </Button>
              </label>
              {fileName && <Chip size="small" variant="outlined" label={fileName} onDelete={reset} />}
              {importing && !preview && <CircularProgress size={18} />}
            </Stack>
          </Stack>

          {preview && (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip color="success" label={`${preview.created} new`} />
                <Chip color="info" label={`${preview.updated} SKU changed`} />
                <Chip variant="outlined" label={`${preview.unchanged} already mapped`} />
                {preview.stockBuffersChanged > 0 && (
                  <Chip
                    color="info"
                    label={`${preview.stockBuffersChanged} stock buffer${plural(preview.stockBuffersChanged)} changed`}
                  />
                )}
                {preview.salesLimitsChanged > 0 && (
                  <Chip
                    color="info"
                    label={`${preview.salesLimitsChanged} max per order changed`}
                  />
                )}
                {preview.errors.length > 0 && <Chip color="error" label={`${preview.errors.length} with problems`} />}
                {blankSkuCount > 0 && <Chip variant="outlined" label={`${blankSkuCount} without a SKU (ignored)`} />}
              </Stack>

              {preview.updated > 0 && (
                <Alert severity="warning">
                  {preview.updated} product{preview.updated === 1 ? "" : "s"} will move to a new SKU. The old SKU stays
                  on {integrationName} with its last stock until it is removed there.
                </Alert>
              )}

              {preview.errors.length > 0 && (
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" fontWeight={600}>
                      These rows will be skipped
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<DownloadOutlined />}
                      onClick={handleDownloadProblems}
                      sx={{ textTransform: "none" }}
                    >
                      Download all {preview.errors.length}
                    </Button>
                  </Stack>
                  <TableContainer sx={{ maxHeight: 280, border: 1, borderColor: "divider", borderRadius: 2 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Row</TableCell>
                          <TableCell>Product ID</TableCell>
                          <TableCell>SKU</TableCell>
                          <TableCell>Problem</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {preview.errors.slice(0, PROBLEMS_SHOWN).map((e) => (
                          <TableRow key={e.rowNumber}>
                            <TableCell>{e.rowNumber}</TableCell>
                            <TableCell sx={{ fontFamily: "monospace" }}>{e.productCode || "—"}</TableCell>
                            <TableCell sx={{ fontFamily: "monospace" }}>{e.externalSku || "—"}</TableCell>
                            <TableCell>{e.message}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {preview.errors.length > PROBLEMS_SHOWN && (
                    <Typography variant="caption" color="text.secondary">
                      Showing the first {PROBLEMS_SHOWN}. Download the list to see them all.
                    </Typography>
                  )}
                </Stack>
              )}
            </Stack>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "space-between", px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={importing} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleSave()}
          disabled={!hasChanges || importing}
          sx={{ textTransform: "none" }}
        >
          {importing && preview ? "Saving…" : hasChanges ? "Save changes" : "Nothing to save"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default BulkMapProductsDialog
